import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Minus, Plus, ShoppingBag, Heart, Truck, RotateCcw } from 'lucide-react';
import useStore from '../store/useStore';
import { translations } from '../i18n/translations';
import { getProduct, getProducts, getCategories } from '../api/api';
import ProductCard from '../components/ProductCard';
import ProductReviews from '../components/ProductReviews';
import { getVATBreakdown } from '../utils/vatCalculations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Helper to resolve image URLs (handles both local uploads and external URLs)
const resolveImageUrl = (url) => {
  if (!url) return 'https://via.placeholder.com/600x800?text=No+Image';
  if (url.startsWith('/api/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { language, addToCart, siteSettings } = useStore();
  const t = translations[language];
  const freeShippingThreshold = siteSettings?.freeShippingThreshold || 75;

  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);
  const [categories, setCategories] = useState([]);

  // Function to get category name from slug
  const getCategoryName = (slug) => {
    if (!slug) return '';
    // First try dynamic categories from API
    const dynamicCat = categories.find(c => c.slug === slug);
    if (dynamicCat) {
      return dynamicCat.translations?.[language]?.name || dynamicCat.translations?.fr?.name || slug;
    }
    // Fallback to translations
    if (t.nav[slug]) {
      return t.nav[slug];
    }
    // Last resort: return the slug itself (capitalized)
    return slug.charAt(0).toUpperCase() + slug.slice(1);
  };

  // Load categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await getCategories();
        if (data && Array.isArray(data)) {
          setCategories(data.filter(cat => !cat.legacy));
        }
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };
    loadCategories();
  }, [language]);

  useEffect(() => {
    const loadProduct = async () => {
      setLoading(true);
      try {
        const data = await getProduct(productId);
        setProduct(data);

        // Set default selections
        if (data.variants?.length > 0) {
          const availableVariant = data.variants.find((v) => v.stock > 0);
          if (availableVariant) {
            setSelectedSize(availableVariant.size);
            setSelectedColor(availableVariant.color);
          }
        }

        // Load related products
        const related = await getProducts({ category: data.category, limit: 4 });
        setRelatedProducts(related.filter((p) => p.id !== productId));
      } catch (error) {
        console.error('Error loading product:', error);
        navigate('/shop');
      } finally {
        setLoading(false);
      }
    };
    loadProduct();
    setCurrentImageIndex(0);
    setQuantity(1);
    setAddedToCart(false);
  }, [productId, navigate]);

  // Reset image index when color changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [selectedColor]);

  if (loading || !product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-700 border-t-transparent"></div>
      </div>
    );
  }

  const translation = product.translations?.[language] || product.translations?.fr || {};
  // Fix: original_price doit être > 0 ET > price pour afficher la promo
  const hasDiscount = product.original_price && product.original_price > 0 && product.original_price > product.price;
  const discount = hasDiscount
    ? Math.round((1 - product.price / product.original_price) * 100)
    : 0;

  // Get unique sizes and colors
  const sizes = [...new Set(product.variants?.map((v) => v.size))];
  const colors = product.variants?.reduce((acc, v) => {
    if (!acc.find((c) => c.name === v.color)) {
      acc.push({ name: v.color, hex: v.color_hex });
    }
    return acc;
  }, []) || [];

  // Get images for selected color (or all images if no color-specific assignment)
  const getImagesForSelectedColor = () => {
    let images = [];
    if (selectedColor && product.color_images && product.color_images[selectedColor]?.length > 0) {
      images = product.color_images[selectedColor];
    } else {
      images = product.images || [];
    }
    // Resolve all image URLs
    return images.map(img => resolveImageUrl(img));
  };

  const displayImages = getImagesForSelectedColor();

  // Check stock for selected variant
  const selectedVariant = product.variants?.find(
    (v) => v.size === selectedSize && v.color === selectedColor
  );
  const isInStock = selectedVariant?.stock > 0;

  const handleAddToCart = () => {
    if (!selectedSize || !selectedColor || !isInStock) return;

    addToCart({
      product_id: product.id,
      size: selectedSize,
      color: selectedColor,
      quantity,
      price: product.price,
      name: translation.name,
      image: displayImages[0] || resolveImageUrl(product.images?.[0]),
    });

    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) =>
      prev === displayImages.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? displayImages.length - 1 : prev - 1
    );
  };

  return (
    <div className="min-h-screen bg-white" data-testid="product-detail-page">
      {/* Breadcrumb */}
      <div className="bg-gray-50 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="text-sm text-gray-500">
            <Link to="/" className="hover:text-amber-700">
              {t.nav.home}
            </Link>
            <span className="mx-2">/</span>
            <Link to="/shop" className="hover:text-amber-700">
              {t.nav.shop}
            </Link>
            <span className="mx-2">/</span>
            <Link to={`/shop/${product.category}`} className="hover:text-amber-700">
              {getCategoryName(product.category)}
            </Link>
            <span className="mx-2">/</span>
            <span className="text-gray-900">{translation.name}</span>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Images */}
          <div>
            <div className="relative aspect-[3/4] bg-gray-100 rounded-lg overflow-hidden">
              <img
                src={displayImages[currentImageIndex] || 'https://via.placeholder.com/600x800'}
                alt={translation.name}
                className="w-full h-full object-cover"
              />
              {displayImages.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}
              {/* Badges */}
              <div className="absolute top-4 left-4 flex flex-col gap-2">
                {hasDiscount && (
                  <span className="bg-red-500 text-white text-sm font-bold px-3 py-1 rounded">
                    -{discount}%
                  </span>
                )}
                {product.new_arrival && (
                  <span className="bg-green-600 text-white text-sm font-bold px-3 py-1 rounded">
                    {t.products.new}
                  </span>
                )}
              </div>
            </div>

            {/* Thumbnails */}
            {displayImages.length > 1 && (
              <div className="flex gap-3 mt-4 overflow-x-auto pb-2">
                {displayImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                      idx === currentImageIndex ? 'border-amber-700 ring-2 ring-amber-300' : 'border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">{translation.name}</h1>
            
            <div className="mb-6">
              <div className="flex items-center gap-4">
                <span className="text-3xl font-bold text-amber-900">
                  €{product.price?.toFixed(2)} <span className="text-base font-normal text-gray-500">TTC</span>
                </span>
                {hasDiscount && (
                  <span className="text-xl text-gray-400 line-through">
                    €{product.original_price?.toFixed(2)}
                  </span>
                )}
              </div>
              <span className="text-sm text-gray-500">
                {(() => {
                  const { htva, tva } = getVATBreakdown(product.price);
                  return `${language === 'fr' ? 'soit' : 'of'} €${htva.toFixed(2)} HTVA + €${tva.toFixed(2)} TVA`;
                })()}
              </span>
            </div>

            <p className="text-gray-600 mb-8">{translation.description}</p>

            {/* Size Selection */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-medium text-gray-900">{t.products.size}</h3>
                <Link to="/size-guide" className="text-sm text-amber-700 hover:text-amber-800">
                  {t.nav.sizeGuide}
                </Link>
              </div>
              <div className="flex flex-wrap gap-2">
                {sizes.map((size) => {
                  const variantInStock = product.variants?.some(
                    (v) => v.size === size && v.stock > 0
                  );
                  return (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      disabled={!variantInStock}
                      className={`px-4 py-2 rounded border transition-colors ${
                        selectedSize === size
                          ? 'bg-amber-700 text-white border-amber-700'
                          : variantInStock
                          ? 'border-gray-300 hover:border-amber-700'
                          : 'border-gray-200 text-gray-300 cursor-not-allowed line-through'
                      }`}
                    >
                      {size}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Color Selection */}
            <div className="mb-6">
              <h3 className="font-medium text-gray-900 mb-3">
                {t.products.color}: <span className="text-gray-600">{selectedColor}</span>
              </h3>
              <div className="flex flex-wrap gap-3">
                {colors.map((color) => {
                  const variantInStock = product.variants?.some(
                    (v) => v.color === color.name && v.stock > 0
                  );
                  return (
                    <button
                      key={color.name}
                      onClick={() => setSelectedColor(color.name)}
                      disabled={!variantInStock}
                      className={`w-10 h-10 rounded-full border-2 transition-all ${
                        selectedColor === color.name
                          ? 'border-amber-700 scale-110'
                          : 'border-gray-300'
                      } ${!variantInStock ? 'opacity-30' : ''}`}
                      style={{ backgroundColor: color.hex }}
                      title={color.name}
                    />
                  );
                })}
              </div>
            </div>

            {/* Quantity */}
            <div className="mb-6">
              <h3 className="font-medium text-gray-900 mb-3">{t.products.quantity}</h3>
              <div className="flex items-center gap-4">
                <div className="flex items-center border rounded">
                  <button
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    className="p-2 hover:bg-gray-100"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="px-4 py-2 min-w-[3rem] text-center">{quantity}</span>
                  <button
                    onClick={() => setQuantity((q) => q + 1)}
                    className="p-2 hover:bg-gray-100"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                {selectedVariant && (
                  <span className="text-sm text-gray-500">
                    {selectedVariant.stock} {t.products.inStock}
                  </span>
                )}
              </div>
            </div>

            {/* Add to Cart */}
            <div className="flex gap-4 mb-8">
              <button
                onClick={handleAddToCart}
                disabled={!isInStock}
                className={`flex-1 flex items-center justify-center gap-2 py-4 rounded-lg font-semibold transition-all ${
                  addedToCart
                    ? 'bg-green-600 text-white'
                    : isInStock
                    ? 'bg-amber-700 hover:bg-amber-800 text-white'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                data-testid="add-to-cart-btn"
              >
                <ShoppingBag className="w-5 h-5" />
                {addedToCart
                  ? (language === 'fr' ? 'Ajouté !' : 'Toegevoegd!')
                  : isInStock
                  ? t.products.addToCart
                  : t.products.outOfStock}
              </button>
              <button className="p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                <Heart className="w-5 h-5" />
              </button>
            </div>

            {/* Features */}
            <div className="border-t pt-6 space-y-4">
              <div className="flex items-center gap-3 text-gray-600">
                <Truck className="w-5 h-5 text-amber-700" />
                <span>
                  {language === 'fr'
                    ? `Livraison gratuite dès ${freeShippingThreshold}€`
                    : `Gratis verzending vanaf €${freeShippingThreshold}`}
                </span>
              </div>
              <div className="flex items-center gap-3 text-gray-600">
                <RotateCcw className="w-5 h-5 text-amber-700" />
                <span>
                  {language === 'fr'
                    ? 'Retours gratuits sous 30 jours'
                    : 'Gratis retour binnen 30 dagen'}
                </span>
              </div>
            </div>

            {/* Details */}
            <div className="border-t mt-6 pt-6">
              <h3 className="font-semibold text-gray-900 mb-4">
                {language === 'fr' ? 'Détails du produit' : 'Productdetails'}
              </h3>
              <dl className="space-y-3">
                <div className="flex">
                  <dt className="w-32 text-gray-500">{t.products.material}:</dt>
                  <dd className="text-gray-900">{translation.material}</dd>
                </div>
                <div className="flex">
                  <dt className="w-32 text-gray-500">{t.products.care}:</dt>
                  <dd className="text-gray-900">{translation.care_instructions}</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>

        {/* Product Reviews */}
        <ProductReviews productId={productId} />

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">
              {language === 'fr' ? 'Vous aimerez aussi' : 'U zult ook leuk vinden'}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.slice(0, 4).map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetailPage;
